import { useMutation, useQuery, useQueryClient } from "react-query";

import axios from "axios";
import { nuclearn_api_url } from "../../App";

// API response types for OAuth providers
export interface OAuthProviderConfigAPI {
  client_id: string;
  client_secret?: string;
  redirect_uri: string;
  scope?: string;
  authority: string;
  metadata: Record<string, string | undefined>;
}

export interface OAuthProviderAPI {
  id: number;
  name: string;
  config: OAuthProviderConfigAPI;
  // ``disabled`` is computed by the backend: true when the IdP's
  // discovery doc is missing endpoints (authorization / token /
  // userinfo). Use this to decide whether a provider's login button
  // should render at all — a "disabled" provider literally can't
  // complete an OAuth flow.
  disabled: boolean;
  // ``disable_local_login`` is admin-controlled and orthogonal to
  // ``disabled``: when true on any enabled provider, the login page
  // hides the password form and the backend refuses ``/auth/login``.
  // The required-claim *rules* themselves are admin-only and never
  // sent here.
  disable_local_login: boolean;
  // Pre-built OIDC Core §5.5 claims request JSON, or null when the
  // provider has the toggle off / nothing to request. When non-null,
  // the FE passes it through as the ``claims`` query parameter on
  // the /authorize redirect. Voluntary requests only (never
  // essential: true) — built on the backend so rule values stay
  // admin-only.
  claims_request: string | null;
}

// One required-claim rule on an OAuthProvider. Mirrors
// backend schema.RequiredClaimRule. Multiple rules on one provider
// are AND-combined at login.
export interface RequiredClaimRuleAPI {
  claim: string;
  operator: "equals" | "not_equals" | "contains" | "not_contains" | "exists";
  value?: unknown;
}

export const CLAIM_OPERATORS: RequiredClaimRuleAPI["operator"][] = [
  "equals",
  "not_equals",
  "contains",
  "not_contains",
  "exists",
];

// Discovery response shape — mirrors backend schema.ProviderDiscovery
export interface ProviderDiscoveryAPI {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint: string | null;
  jwks_uri: string;
  token_endpoint_auth_methods_supported: string[];
  scopes_supported: string[];
  claims_supported: string[];
  code_challenge_methods_supported: string[];
  grant_types_supported: string[];
  response_types_supported: string[];
}

export interface DiscoverProviderResponseAPI {
  discovery: ProviderDiscoveryAPI;
  suggested_token_endpoint_auth_method: string;
  suggested_scope: string;
  suggested_group_claim_name: string;
  warnings: string[];
}

const discoverProviderConfig = async (
  well_known_url: string,
): Promise<DiscoverProviderResponseAPI> => {
  const response = await axios.post(
    `${nuclearn_api_url}/auth/discover-provider-config`,
    { well_known_url },
  );
  return response.data;
};

export const useDiscoverProviderConfig = () => {
  return useMutation((well_known_url: string) =>
    discoverProviderConfig(well_known_url),
  );
};

// Admin provider view — includes group config + observed-group
// diagnostics that the public /list-providers endpoint strips.
export interface AdminOAuthProviderAPI {
  id: number;
  name: string;
  client_id: string;
  client_secret: string;
  well_known_url: string;
  redirect_uri: string;
  scope: string | null;
  token_endpoint_auth_method: string;
  group_role_mappings: Record<string, string> | null;
  group_claim_name: string | null;
  last_observed_group_claims: string[] | null;
  // Boolean only — the actual PEM is never sent to the client.
  // ``true`` means the admin has previously uploaded a private key for
  // ``private_key_jwt``; ``false`` means none is stored.
  has_client_private_key: boolean;
  token_endpoint_auth_signing_alg: string | null;
  required_claims: RequiredClaimRuleAPI[] | null;
  disable_local_login: boolean;
  enabled: boolean;
  request_claims_explicitly: boolean;
}

const getAdminProviders = async (): Promise<AdminOAuthProviderAPI[]> => {
  const response = await axios.get(`${nuclearn_api_url}/auth/providers-admin`);
  return response.data;
};

export const useGetAdminProviders = () => {
  return useQuery("admin-providers", () => getAdminProviders(), {
    refetchOnWindowFocus: false,
  });
};

export interface RoleAPI {
  id: number;
  name: string;
  description: string | null;
}

const getAvailableRoles = async (): Promise<RoleAPI[]> => {
  const response = await axios.get(`${nuclearn_api_url}/auth/available-roles`);
  return response.data;
};

export const useGetAvailableRoles = () => {
  return useQuery("oauth-available-roles", () => getAvailableRoles(), {
    refetchOnWindowFocus: false,
  });
};

// Provider diagnostics — read-only checks against an IdP without
// doing a full OAuth round-trip.
export interface DiagnosticCheckAPI {
  name: string;
  passed: boolean;
  severity: "error" | "warning" | "info";
  detail: string;
  fix_hint: string | null;
}

export interface ProviderDiagnosticResultAPI {
  provider_id: number;
  provider_name: string;
  overall_passed: boolean;
  checks: DiagnosticCheckAPI[];
}

const testProvider = async (
  provider_id: number,
): Promise<ProviderDiagnosticResultAPI> => {
  const response = await axios.post(
    `${nuclearn_api_url}/auth/test-provider?provider_id=${provider_id}`,
  );
  return response.data;
};

export const useTestProvider = () => {
  return useMutation((provider_id: number) => testProvider(provider_id));
};

export const getProviders = async (): Promise<OAuthProviderAPI[]> => {
  const response = await axios.get(`${nuclearn_api_url}/auth/list-providers`);
  return response.data;
};

export const useGetProviders = () => {
  return useQuery("providers", () => getProviders(), {
    refetchOnWindowFocus: false,
  });
};

// create a new provider
const createProvider = async (new_provider: any) => {
  // Send only what the admin form collected. The backend
  // ``NewOAuthProvider`` schema accepts blank ``scope`` /
  // ``token_endpoint_auth_method`` and auto-detects from the
  // well-known doc; we don't need to seed the legacy ``issuer``,
  // ``authorization_endpoint``, etc. fields that the current form
  // doesn't surface (they aren't part of NewOAuthProvider anyway).
  const response = await axios.post(
    `${nuclearn_api_url}/auth/create-provider`,
    new_provider,
  );
  return response.data;
};

export const useCreateProvider = () => {
  const queryClient = useQueryClient();
  return useMutation((new_provider: any) => createProvider(new_provider), {
    onSuccess: () => {
      // Invalidate BOTH provider queries:
      //  - "providers" feeds the public login page (/list-providers)
      //  - "admin-providers" feeds the admin config UI (/providers-admin)
      // The admin page reads server-computed fields like
      // ``has_client_private_key`` that won't reflect in the UI without
      // an "admin-providers" refetch.
      queryClient.invalidateQueries("providers");
      queryClient.invalidateQueries("admin-providers");
    },
  });
};

// update an existing provider
const updateProvider = async (provider: any) => {
  // Only required by the create-provider path: name, client_id,
  // client_secret, well_known_url, redirect_uri. Update accepts
  // partial bodies — we explicitly avoid pre-seeding fields the
  // current admin form doesn't collect (issuer, token_endpoint,
  // authorization_endpoint, userinfo_endpoint) so we don't clobber
  // them with empty strings on PATCH.
  const providerData = { ...provider };
  if (
    typeof providerData.client_secret === "string" &&
    providerData.client_secret.includes("****")
  ) {
    delete providerData.client_secret;
  }
  const response = await axios.patch(
    `${nuclearn_api_url}/auth/update-provider?provider_id=${provider.providerId}`,
    providerData,
  );
  return response.data;
};

export const useUpdateProvider = () => {
  const queryClient = useQueryClient();
  return useMutation((provider: any) => updateProvider(provider), {
    onSuccess: () => {
      queryClient.invalidateQueries("providers");
      queryClient.invalidateQueries("admin-providers");
    },
  });
};

const deleteProvider = async (provider_id: number) => {
  const response = await axios.delete(
    `${nuclearn_api_url}/auth/delete-provider?provider_id=${provider_id}`,
  );
  return response.data;
};

export const useDeleteProvider = () => {
  const queryClient = useQueryClient();
  return useMutation((provider_id: number) => deleteProvider(provider_id), {
    onSuccess: () => {
      queryClient.invalidateQueries("providers");
      queryClient.invalidateQueries("admin-providers");
    },
  });
};
